import {
  Avatar,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  useColorMode,
  Portal,
} from '@chakra-ui/react'

import { SidebarResponsive } from 'components/sidebar/Sidebar'
import PropTypes from 'prop-types'
import React from 'react'
// Assets
import { IoMdMoon, IoMdSunny } from 'react-icons/io'
import { IoOpenOutline } from 'react-icons/io5'
import sidebarRoutes from 'components/sidebar/adminRoutes'
import { useNavigate } from 'react-router-dom'
import { signOut, useAuthStore } from 'store/auth.store'

export default function HeaderLinks (props) {
  const { colorMode, toggleColorMode } = useColorMode()
  const navigate = useNavigate()

  // Retrieve user data from the Auth Store
  const { user } = useAuthStore()
  const dynamicUserName = user?.username || 'User'

  // --- Refined Style Variables for Cleaner Look ---
  const navbarIcon = useColorModeValue('gray.600', 'white')
  const menuBg = useColorModeValue('white', 'gray.800')
  const textColor = useColorModeValue('gray.800', 'white')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const menuShadow = useColorModeValue(
    '0px 8px 24px -8px rgba(0, 0, 0, 0.1)',
    '0px 8px 24px -8px rgba(0, 0, 0, 0.4)',
  )

  // Dynamic Avatar Background Color
  const avatarBg = useColorModeValue('brand.500', 'brand.600')

  const handleSignOut = () => {
    signOut()
    navigate('/auth/sign-in')
  }

  // New handler for navigating to the live site
  const handleGoToSite = () => {
    window.open('https://bamyanentertainment.de/', '_blank')
  }

  return (
    <Flex
      w={{ sm: '100%', md: 'auto' }}
      alignItems='center'
      flexDirection='row'
      bg='transparent'
      flexWrap='nowrap'
      p='0px'
      boxShadow='none'
      gap='6px'
    >
      <SidebarResponsive routes={sidebarRoutes} />

      {/* 1. GO TO SITE BUTTON */}
      <Button
        variant='ghost'
        bg='transparent'
        p='0px'
        minW='unset'
        minH='unset'
        h='40px'
        w='40px'
        borderRadius='full'
        onClick={handleGoToSite}
        title='Go to Live Site'
        _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
        _active={{ bg: useColorModeValue('gray.200', 'gray.600') }}
      >
        <Icon h='20px' w='20px' color={navbarIcon} as={IoOpenOutline} />
      </Button>

      {/* 3. THEME TOGGLE BUTTON */}
      <Button
        variant='ghost'
        bg='transparent'
        p='0px'
        minW='unset'
        minH='unset'
        h='40px'
        w='40px'
        borderRadius='full'
        onClick={toggleColorMode}
        _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
        _active={{ bg: useColorModeValue('gray.200', 'gray.600') }}
      >
        <Icon
          h='20px'
          w='20px'
          color={navbarIcon}
          as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
        />
      </Button>

      {/* 3. PROFILE & LOGOUT MENU */}
      <Menu>
        <MenuButton p='0px'>
          <Avatar
            // 💡 APPLYING NEW STYLES: Dynamic background color
            color='white'
            name={dynamicUserName}
            bg={avatarBg}
            size='sm'
            w='40px'
            h='40px'
            // 💡 NEW: Subtle ring on hover to indicate clickability
            _hover={{
              cursor: 'pointer',
              boxShadow: `0 0 0 3px ${useColorModeValue(
                'rgba(66, 153, 225, 0.6)',
                'rgba(144, 205, 244, 0.6)',
              )}`, // blue focus ring
            }}
          />
        </MenuButton>
        <Portal>
          <MenuList
            boxShadow={menuShadow}
            p='0px'
            mt='10px'
            borderRadius='10px'
            bg={menuBg}
            border={`1px solid ${borderColor}`}
            zIndex='9999'
          >
            {/* Dynamic Name Header */}
            <Flex w='100%'>
              <Text
                ps='16px'
                pt='12px'
                pb='8px'
                w='100%'
                borderBottom={`1px solid ${borderColor}`}
                fontSize='sm'
                fontWeight='600'
                color={textColor}
              >
                👋 Hi, {dynamicUserName}
              </Text>
            </Flex>

            <Flex flexDirection='column' p='5px'>
              {/* LOGOUT BUTTON */}
              <MenuItem
                _hover={{ bg: 'red.500', color: 'white' }}
                _focus={{ bg: 'red.500', color: 'white' }}
                color='red.500'
                borderRadius='8px'
                px='14px'
                onClick={handleSignOut}
              >
                <Text fontSize='sm'>Log out</Text>
              </MenuItem>
            </Flex>
          </MenuList>
        </Portal>
      </Menu>
    </Flex>
  )
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
}
